import { Component, OnInit } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { MatSnackBar } from "@angular/material/snack-bar";
import { AdminAdmGroupService } from "../../../../api/services/admin-adm-group.service";
import { AdminAdmDefinitionService } from "../../../../api/services/admin-adm-definition.service";
import { AdminInteractiveSubService } from "../../../../api/services/admin-interactive-sub.service";
import { AdmGroupEx } from "../../../../api/models/adm-group-ex";
import { AdmTable } from "../../../../api/models/adm-table";
import { AdmTableColumn } from "../../../../api/models/adm-table-column";
import { InteractSubTypes } from "../../../../api/models/interact-sub-types";
import { AdmLayoutLite } from "../../../../api/models/adm-layout-lite";
import { DialogResultType } from "../../../../dialogs/DialogResult";
import { AdmGroupDialogComponent } from "../../../../dialogs/adm-group-dialog/adm-group-dialog.component";

// Admin management of Agent Data Model Groups (named sets of tables) and their attachment to plan types -
// a plan type with no Groups can create no Agent Data Models.
@Component({
  selector: "admin-adm-groups",
  templateUrl: "./admin-adm-groups.component.html",
})
export class AdminAdmGroupsComponent implements OnInit {
  public loading = true;
  // Separate from `loading` (which only tracks the Groups list): the New/Edit Group dialog hands its "tables"
  // and "columns" catalog to the dialog as a plain snapshot, not a live binding, so opening it before this
  // finishes freezes the dialog with an empty table picker forever - never self-corrects once `tables` is
  // reassigned later, even though `tableName()` lookups elsewhere (which re-read `this.tables` live on every
  // call) do. Gate New/Edit on this instead of guessing at timing.
  public catalogLoaded = false;
  public groups: AdmGroupEx[] = [];
  public tables: AdmTable[] = [];
  public columns: AdmTableColumn[] = [];
  public planTypes: InteractSubTypes[] = [];

  // plan type attachment
  public selectedPlanTypeId: number | null = null;
  public planTypeGroupIds: number[] = [];
  public planTypeAllowSelfServiceCreate = true;
  public planTypeAdmLayoutIds: number[] = [];
  public subscriptionAdms: AdmLayoutLite[] = [];

  constructor(
    private groupService: AdminAdmGroupService,
    private definitionService: AdminAdmDefinitionService,
    private interactiveSubService: AdminInteractiveSubService,
    private snackBar: MatSnackBar,
    private dialog: MatDialog
  ) {}

  ngOnInit() {
    this.load();
    this.definitionService.adminAdmDefinitionGetDefinition().subscribe((d) => {
      this.tables = (d.tables || []).filter((t) => !t.retired);
      this.columns = (d.columns || []).filter((c) => !c.retired);
      this.catalogLoaded = true;
    });
    this.interactiveSubService.adminInteractiveSubGetInteractSubTypes().subscribe((types) => {
      this.planTypes = types || [];
    });
    this.groupService.adminAdmGroupGetSubscriptionAdms().subscribe((layouts) => {
      this.subscriptionAdms = layouts || [];
    });
  }

  load() {
    this.loading = true;
    this.groupService.adminAdmGroupGetGroups().subscribe({
      next: (groups) => { this.groups = groups || []; this.loading = false; },
      error: () => { this.loading = false; this.snackBar.open("Could not load Groups", null, { duration: 5000 }); },
    });
  }

  tableName(id: number): string {
    const t = this.tables.find((x) => x.admTableId === id);
    return t ? t.tableName : `#${id}`;
  }

  groupName(id: number): string {
    const g = this.groups.find((x) => x.admGroupId === id);
    return g ? g.name : `#${id}`;
  }

  columnLabel(c: AdmTableColumn): string {
    return `${this.tableName(c.admTableId)}.${c.outputName || c.columnName}`;
  }

  // ---- groups -----------------------------------------------------------------------------------------
  newGroup() {
    if (!this.catalogLoaded) return;
    this.openGroupDialog({ admGroupId: 0, name: "", description: "", sortOrder: 0, idleDays: null, active: true, tableIds: [], allowedColumnIds: [] });
  }

  editGroup(group: AdmGroupEx) {
    if (!this.catalogLoaded) return;
    this.openGroupDialog({ ...group, tableIds: [...(group.tableIds || [])], allowedColumnIds: [...(group.allowedColumnIds || [])] });
  }

  private openGroupDialog(group: AdmGroupEx) {
    AdmGroupDialogComponent.showDialog(this.dialog, { group, tables: this.tables, columns: this.columns, tableName: (id) => this.tableName(id) }).subscribe((result) => {
      if (!result || result.ResultType !== DialogResultType.Ok) return;
      this.groupService.adminAdmGroupSaveGroup(result.ResultData).subscribe({
        next: () => { this.snackBar.open("Group saved", null, { duration: 3000 }); this.load(); },
        error: (e) => this.failed(e),
      });
    });
  }

  deleteGroup(group: AdmGroupEx) {
    if (!confirm(`Delete the Group "${group.name}"?`)) return;
    this.groupService.adminAdmGroupDeleteGroup(group.admGroupId).subscribe({
      next: () => { this.snackBar.open("Group deleted", null, { duration: 3000 }); this.load(); },
      error: (e) => this.failed(e),
    });
  }

  // ---- plan types -------------------------------------------------------------------------------------
  onPlanTypeChange() {
    this.planTypeGroupIds = [];
    this.planTypeAdmLayoutIds = [];
    this.planTypeAllowSelfServiceCreate = true;
    if (this.selectedPlanTypeId === null) return;
    this.groupService.adminAdmGroupGetPlanTypeGroups(this.selectedPlanTypeId).subscribe((ids) => {
      this.planTypeGroupIds = ids || [];
    });
    this.groupService.adminAdmGroupGetPlanTypeAllowSelfServiceCreate(this.selectedPlanTypeId).subscribe((allow) => {
      this.planTypeAllowSelfServiceCreate = allow;
    });
    this.groupService.adminAdmGroupGetPlanTypeAdmLayouts(this.selectedPlanTypeId).subscribe((ids) => {
      this.planTypeAdmLayoutIds = ids || [];
    });
  }

  savePlanTypeGroups() {
    if (this.selectedPlanTypeId === null) return;
    const id = this.selectedPlanTypeId;
    this.groupService.adminAdmGroupSetPlanTypeGroups({ interactSubTypeId: id, groupIds: this.planTypeGroupIds }).subscribe({
      next: () => {
        this.groupService.adminAdmGroupSetPlanTypeAllowSelfServiceCreate({ interactSubTypeId: id, allow: this.planTypeAllowSelfServiceCreate }).subscribe({
          next: () => {
            this.groupService.adminAdmGroupSetPlanTypeAdmLayouts({ interactSubTypeId: id, admLayoutIds: this.planTypeAdmLayoutIds }).subscribe({
              next: () => this.snackBar.open("Plan type saved", null, { duration: 3000 }),
              error: (e) => this.failed(e),
            });
          },
          error: (e) => this.failed(e),
        });
      },
      error: (e) => this.failed(e),
    });
  }

  private failed(error: any) {
    const message = (error && (error.error || error.message)) || "Request failed";
    this.snackBar.open(typeof message === "string" ? message : "Request failed", null, { duration: 8000 });
  }
}
